import React, { useMemo } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import intl from "react-intl-universal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { groupBy } from "lodash-es";
import { makeStyles } from "@material-ui/core/styles";

import { IWithballData } from "types";
import { StatisticsBoard } from "components/StatisticsBoard";
import { BAR_COLOR } from "MuiProvider/colors";
import { calculatePecentage, groupPossessionStrive } from "utils";
import { EVENT_I18N_TAGS } from "modules/withball-data-input/constants";

interface IMyPossessionPanelProps {
  dataArray: IWithballData[];
}

const ASPECT = 2;
const CHART_WIDTH = "100%";
const MARGIN = { top: 20, right: 5, left: 5, bottom: 5 };
const BAR_SIZE = 20;

const useStyles = makeStyles(() => ({
  bottomAlignGrid: {
    position: "relative",
  },
  bottomAlignContent: {
    position: "absolute",
    bottom: 0,
    height: "auto !important",
  },
}));

export const MyPossessionPanel = ({ dataArray }: IMyPossessionPanelProps) => {
  const classes = useStyles();
  const { win, lose } = useMemo(() => {
    return groupPossessionStrive(dataArray);
  }, [dataArray]);

  const renderPossessionChart = (
    isWinPossession: boolean,
    isSelfHalf: boolean = false
  ) => {
    const typedData = isWinPossession ? win : lose;
    const halfData = isSelfHalf ? typedData.selfHalf : typedData.otherHalf;
    const groupedData = groupBy(halfData, (data) => data.action);

    const data = Object.getOwnPropertyNames(groupedData).map((actionId) => {
      return {
        action: intl.get(EVENT_I18N_TAGS[parseInt(actionId)]),
        total: groupedData[actionId].length,
      };
    });
    return (
      <ResponsiveContainer
        width={CHART_WIDTH}
        aspect={ASPECT}
        className={classes.bottomAlignContent}
      >
        <BarChart data={data} margin={MARGIN}>
          <XAxis dataKey="action" interval={0} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant="h4">{`${intl.get("STATS_POSSESSION_WIN")}: ${
          win.total
        }`}</Typography>
        <Typography>{`${intl.get("STATS_POS_SELF")}: ${
          win.selfHalf.length
        } (${calculatePecentage(win.selfHalf.length, win.total)})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={win.selfHalf} />
          </Grid>
          <Grid item xs={6} className={classes.bottomAlignGrid}>
            {renderPossessionChart(true, true)}
          </Grid>
        </Grid>
        <Box mt={2} />
        <Typography>{`${intl.get("STATS_POS_OTHER")}: ${
          win.otherHalf.length
        } (${calculatePecentage(
          win.otherHalf.length,
          win.total
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={win.otherHalf} />
          </Grid>
          <Grid item xs={6} className={classes.bottomAlignGrid}>
            {renderPossessionChart(true, false)}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h4">{`${intl.get("STATS_POSSESSION_LOSE")}: ${
          lose.total
        }`}</Typography>
        <Typography>{`${intl.get("STATS_POS_SELF")}: ${
          lose.selfHalf.length
        } (${calculatePecentage(
          lose.selfHalf.length,
          lose.total
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={lose.selfHalf} />
          </Grid>
          <Grid item xs={6} className={classes.bottomAlignGrid}>
            {renderPossessionChart(false, true)}
          </Grid>
        </Grid>
        <Box mt={2} />
        <Typography>{`${intl.get("STATS_POS_OTHER")}: ${
          lose.otherHalf.length
        } (${calculatePecentage(
          lose.otherHalf.length,
          lose.total
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={lose.otherHalf} />
          </Grid>
          <Grid item xs={6} className={classes.bottomAlignGrid}>
            {renderPossessionChart(false, false)}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
